/**
 * Centralized Supabase API helper functions
 * This file provides fetch-based API calls as a workaround for the Supabase 
 * client hanging issue in some scenarios.
 */

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Get the current auth token from localStorage
 * @returns {string|null} The access token or null if not authenticated
 */
export const getAuthToken = () => {
  try {
    const storageKey = 'sb-' + supabaseUrl.replace('https://', '').split('.')[0] + '-auth-token';
    const sessionData = localStorage.getItem(storageKey);
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      return parsed?.access_token || null;
    }
  } catch (e) {
    console.warn('Error getting auth token:', e);
  }
  return null;
};

/**
 * Build common headers for Supabase REST API calls
 * @param {boolean} includeAuth - Whether to include the auth token
 * @returns {Object} Headers object
 */
export const buildHeaders = (includeAuth = false) => {
  const headers = {
    'apikey': supabaseAnonKey,
    'Content-Type': 'application/json',
  };
  
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

/**
 * Fetch data from Supabase REST API using fetch (workaround for client hanging)
 * @param {string} endpoint - The REST endpoint path (e.g., '/rest/v1/profiles')
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} The response data
 */
export const supabaseFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${supabaseUrl}${endpoint}`;
  
  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        ...buildHeaders(options.includeAuth !== false),
        ...options.headers,
      },
    });
  } catch (networkError) {
    throw new Error(`Network error: ${networkError.message}`);
  }
  
  // Read the response body only once
  let text;
  try {
    text = await response.text();
  } catch (readError) {
    // Body might have already been read - try to get any available info
    console.warn('Could not read response body:', readError);
    text = '';
  }
  
  if (!response.ok) {
    throw new Error(`Supabase API error: ${response.status} - ${text || 'Unknown error'}`);
  }
  
  // Handle empty responses
  if (!text) {
    return null;
  }
  
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
};

/**
 * GET request to Supabase REST API
 * @param {string} table - Table name
 * @param {string} query - Query parameters (e.g., 'select=*&id=eq.123')
 * @param {boolean} includeAuth - Whether to include auth token
 * @returns {Promise<any>} The response data
 */
export const supabaseGet = async (table, query = 'select=*', includeAuth = true) => {
  return supabaseFetch(`/rest/v1/${table}?${query}`, {
    method: 'GET',
    includeAuth,
  });
};

/**
 * POST request to Supabase REST API
 * @param {string} table - Table name
 * @param {Object} data - Data to insert
 * @param {boolean} includeAuth - Whether to include auth token
 * @returns {Promise<any>} The response data
 */
export const supabasePost = async (table, data, includeAuth = true) => {
  return supabaseFetch(`/rest/v1/${table}`, {
    method: 'POST',
    body: JSON.stringify(data),
    includeAuth,
    headers: {
      'Prefer': 'return=representation',
    },
  });
};

/**
 * PATCH request to Supabase REST API
 * @param {string} table - Table name
 * @param {string} query - Query parameters for filtering (e.g., 'id=eq.123')
 * @param {Object} data - Data to update
 * @param {boolean} includeAuth - Whether to include auth token
 * @returns {Promise<any>} The response data
 */
export const supabasePatch = async (table, query, data, includeAuth = true) => {
  return supabaseFetch(`/rest/v1/${table}?${query}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    includeAuth,
    headers: {
      'Prefer': 'return=representation',
    },
  });
};

/**
 * Upload file to Supabase Storage
 * @param {string} bucket - Bucket name
 * @param {string} fileName - File path/name in the bucket
 * @param {File} file - The file to upload
 * @returns {Promise<{publicUrl: string}>} Object with public URL
 */
export const supabaseUpload = async (bucket, fileName, file) => {
  const token = getAuthToken();
  
  const response = await fetch(
    `${supabaseUrl}/storage/v1/object/${bucket}/${fileName}`,
    {
      method: 'POST',
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${token}`,
        'Content-Type': file.type,
        // Nessuna sovrascrittura: i nomi file sono unici e lo storage concede solo
        // l'inserimento (con upsert servirebbe anche il permesso di modifica).
        'x-upsert': 'false',
      },
      body: file,
    }
  );
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload error: ${response.status} - ${errorText}`);
  }
  
  const publicUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${fileName}`;
  return { publicUrl };
};

/**
 * Fetch user profile by ID using REST API (workaround for client hanging)
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>} The profile data or null
 */
export const fetchProfileById = async (userId) => {
  try {
    const data = await supabaseGet('profiles', `id=eq.${userId}&select=*`);
    // Handle empty array (profile not found/not created yet)
    if (!data || (Array.isArray(data) && data.length === 0)) {
      console.log('Profile not found for user:', userId);
      return null;
    }
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    // Don't log as error if it's just a "not found" case
    if (error.message?.includes('404') || error.message?.includes('not found')) {
      console.log('Profile not yet created for user:', userId);
    } else {
      console.error('Error fetching profile:', error);
    }
    return null;
  }
};

export { supabaseUrl, supabaseAnonKey };
